import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, Loader2 } from "lucide-react";
import { getTargets, putTargets } from "../../lib/workcapApi";
import { cn } from "../../lib/utils";

const FIELDS = [["dio", "DIO target (days)", "Lower is better"], ["ccc", "CCC target (days)", "Lower is better"], ["gmroi", "GMROI target", "Higher is better"]];
const when = (iso) => new Date(iso).toLocaleString("en-PH", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

// Edit the three working-capital targets with a reason; shows the change history.
export function TargetsForm({ onSaved }) {
  const [d, setD] = useState(null);
  const [vals, setVals] = useState({});
  const [reason, setReason] = useState("");
  const [state, setState] = useState({ busy: false, error: null, msg: null, changed: false });
  const load = () => getTargets().then(x => { setD(x); setVals({ dio: x.targets.dio, ccc: x.targets.ccc, gmroi: x.targets.gmroi }); }).catch(e => setState(s => ({ ...s, error: e.message })));
  useEffect(() => { load(); }, []);
  const save = async () => {
    setState({ busy: true, error: null, msg: null, changed: false });
    try {
      const r = await putTargets({ ...Object.fromEntries(Object.entries(vals).map(([k, v]) => [k, Number(v)])), reason });
      setD(x => ({ ...x, targets: r.targets, history: r.history }));
      if (r.changed) { setReason(""); onSaved?.(); }
      setState({ busy: false, error: null, msg: r.message, changed: r.changed });
    } catch (e) { setState({ busy: false, error: e.message, msg: null, changed: false }); }
  };
  if (!d) return <div className="text-sm text-muted" data-testid="wc-targets-loading">{state.error || "Loading targets…"}</div>;
  return (
    <div className="space-y-4 text-sm" data-testid="wc-targets-form">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {FIELDS.map(([k, l, h]) => (
          <label key={k} className="block text-[12px]">{l}<span className="block text-[10.5px] text-muted">{h}</span>
            <input data-testid={`wc-target-${k}`} type="number" step={k === "gmroi" ? "0.05" : "1"} disabled={!d.can_edit} value={vals[k] ?? ""}
                   onChange={e => setVals(v => ({ ...v, [k]: e.target.value }))}
                   className="mt-1 w-full h-9 bg-surface border border-border rounded-sm px-2.5 font-mono-tabular focus:outline-none focus:border-primary" /></label>
        ))}
      </div>
      {d.can_edit && <>
        <label className="block text-[12px]">Reason (at least 10 characters)
          <input data-testid="wc-target-reason" value={reason} onChange={e => setReason(e.target.value)} maxLength={300}
                 className="mt-1 w-full h-9 bg-surface border border-border rounded-sm px-2.5 focus:outline-none focus:border-primary" /></label>
        {state.error && <div className="text-critical text-[12px]" data-testid="wc-target-error">{state.error}</div>}
        {state.msg && <div className={cn("text-[12px]", state.changed ? "text-success" : "text-muted")} data-testid={state.changed ? "wc-target-saved" : "wc-target-unchanged"}>{state.msg}</div>}
        <button data-testid="wc-target-save" disabled={state.busy || reason.trim().length < 10} onClick={save}
                className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-sm text-[12.5px] font-medium bg-primary text-primary-fg disabled:opacity-45">
          {state.busy && <Loader2 size={13} className="animate-spin" />}Save targets</button>
      </>}
      {!d.can_edit && <div className="text-[11.5px] text-muted">View only — the CFO, COO or IT Head changes targets.</div>}
      <div><div className="kpi-label mb-1">Change history</div>
        {!d.history.length ? <div className="text-[12px] text-muted">No changes yet — defaults DIO 60 d, CCC 60 d, GMROI 1.50.</div> : (
          <div className="overflow-x-auto"><table className="w-full text-[12px]" data-testid="wc-target-history"><tbody>
            {d.history.map((h, i) => <tr key={i} className="border-t border-border/60"><td className="py-1.5 pr-2 font-mono-tabular whitespace-nowrap">{when(h.at)}</td>
              <td className="py-1.5 pr-2 uppercase">{h.kpi}</td><td className="py-1.5 pr-2 font-mono-tabular whitespace-nowrap">{h.before} → {h.after}</td>
              <td className="py-1.5 pr-2 font-mono-tabular">{h.by}</td><td className="py-1.5 text-muted">{h.reason}</td></tr>)}
          </tbody></table></div>)}
      </div>
    </div>
  );
}

export function TargetsPanel({ onClose, onSaved }) {
  return createPortal(
    <div className="fixed inset-0 z-[60]" data-testid="wc-targets-panel">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <aside className={cn("absolute right-0 top-0 h-full w-full sm:w-[560px] bg-bg border-l border-border shadow-2xl overflow-y-auto fc-slide-in p-5 space-y-4")}>
        <div className="flex items-start justify-between"><div className="font-display text-lg font-semibold">Working-capital targets</div>
          <button data-testid="wc-targets-close" onClick={onClose} aria-label="Close" className="h-8 w-8 inline-flex items-center justify-center border border-border rounded-sm"><X size={15} /></button></div>
        <TargetsForm onSaved={onSaved} />
      </aside>
    </div>, document.body);
}
